import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { InstantSearch, Configure, useHits } from 'react-instantsearch-hooks-web';

const INDEX_NAME = process.env.REACT_APP_ALGOLIA_INDEX_NAME;
const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_SEARCH_KEY
);

/**
 * 검색 결과 유무를 판별하고 리스트를 렌더링하는 컴포넌트
 */
function CustomHits({ transformItems }) {
  const { hits } = useHits({ transformItems });

  // 검색 결과가 없을 때 NotFoundPage와 동일한 UI 노출
  if (hits.length === 0) {
    return (
      <div className="prep">
        검색 결과가 없습니다.
      </div>
    );
  }

  return (
    <div className="ais-Hits">
      <ul className="ais-Hits-list">
        {hits.map((hit) => (
          <li key={hit.objectID} className="ais-Hits-item">
            <HitItem hit={hit} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Search() {
  const [searchParams] = useSearchParams();
  const queryFromUrl = searchParams.get('q') || '';

  const initialUiState = useMemo(() => ({
    [INDEX_NAME]: { query: queryFromUrl },
  }), [queryFromUrl]);

  // 대메뉴(카테고리 루트) 항목만 제외하고 필터링 없이 그대로 출력합니다.
  const transformItems = (items) => items.filter(hit => !!hit.parent_title);

  return (
    <section className="search_page">
      <div className="search_wrapper">
        <InstantSearch searchClient={searchClient} indexName={INDEX_NAME} initialUiState={initialUiState}>
          <Configure query={queryFromUrl} attributesToSnippet={['content:1000']} /> 
          <div className="results_container">
            <CustomHits transformItems={transformItems} />
          </div>
        </InstantSearch>
      </div>
    </section>
  );
}

function HitItem({ hit }) {
  /**
   * HTML 엔티티 복구, 1글자 강조 제거, SVG 보존 및 글자수 제한
   */
  const processFinalHtml = (rawStr) => {
    if (!rawStr) return "";

    // 1. HTML 엔티티 복구
    let decoded = rawStr
      .replace(/&amp;lt;/g, '<').replace(/&amp;gt;/g, '>')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&amp;quot;/g, '"').replace(/&quot;/g, '"')
      .replace(/&amp;amp;/g, '&').replace(/&amp;/g, '&');

    // 2. 1글자 강조(em) 제거 (노이즈 방지)
    decoded = decoded.replace(/<em>([^]{1})<\/em>/g, '$1');

    // 3. SVG 태그 보호 및 텍스트 컷
    const svgMatches = decoded.match(/<svg[\s\S]*?<\/svg>/g) || [];
    let textOnly = decoded.replace(/<svg[\s\S]*?<\/svg>/g, '__SVG_PLACEHOLDER__');

    if (textOnly.length > 150) {
      textOnly = textOnly.substring(0, 150) + "...";
    }

    let finalHtml = textOnly;
    svgMatches.forEach(svg => {
      finalHtml = finalHtml.replace('__SVG_PLACEHOLDER__', svg);
    });
    finalHtml = finalHtml.replace(/__SVG_PLACEHOLDER__/g, '');

    return finalHtml;
  };

  const displayTitle = processFinalHtml(hit._highlightResult?.title?.value || hit.title);
  
  const snippetValue = hit._snippetResult?.content?.value;
  const contentValue = hit.content || "";
  const targetContent = (hit._snippetResult?.content?.matchLevel !== 'none' && snippetValue) 
    ? snippetValue 
    : contentValue;

  const displayContent = processFinalHtml(targetContent);

  // 경로 변환 로직
  const transformPath = (path) => {
    if (!path) return "/";
    const parts = path.split('/'); 
    const newPath = [parts[1], parts[3]].filter(Boolean).join('/');
    return `/${newPath}`;
  };

  return (
    <div className="hit_item">
      <a href={transformPath(hit.path)}>
        <div className="hit_breadcrumb">
          {hit.parent_title}
        </div>
        <h3 dangerouslySetInnerHTML={{ __html: displayTitle }} />
        <div className="hit_content" dangerouslySetInnerHTML={{ __html: displayContent }} />
      </a>
    </div>
  );
}

export default Search;